import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Sub-pixel slack: browsers round layout metrics, so a value that fits can
 * still report a 1px difference between scroll and client size.
 */
const TOLERANCE_PX = 1;

/** Ratio browsers use for `line-height: normal` at typical body sizes. */
const NORMAL_LINE_HEIGHT_RATIO = 1.5;

/**
 * One line of the element, in px, or 0 when it cannot be resolved.
 *
 * `parseFloat(lineHeight)` is `NaN` for the computed keyword `normal`, and
 * every comparison against `NaN` is false — which would silently disable the
 * height axis of the measurement. Fall back to the font size, and to skipping
 * the height axis entirely if even that is unavailable (jsdom).
 */
function resolveLineHeight(el: HTMLElement): number {
    const computed = getComputedStyle(el);
    const lineHeight = parseFloat(computed.lineHeight);
    if (Number.isFinite(lineHeight)) {
        return lineHeight;
    }
    const fontSize = parseFloat(computed.fontSize);
    return Number.isFinite(fontSize) ? fontSize * NORMAL_LINE_HEIGHT_RATIO : 0;
}

/**
 * Reports whether `value` needs more than one line inside the element that
 * `textRef` is attached to.
 *
 * The measurement is state-independent on purpose: horizontal overflow catches
 * the collapsed `nowrap` state, height beyond one line catches the wrapped
 * (expanded or mid-animation) states. A state-specific measure makes the
 * expand affordance flicker while the row animates.
 *
 * Re-measures when the value changes, when the webfont swaps in (a borderline
 * value fits the fallback font and overflows the real one), and when the
 * element resizes — the sidebar is 300/330/380px wide depending on viewport,
 * and the same rows render inside a modal.
 *
 * Inert wherever layout cannot be measured: `ResizeObserver` and
 * `document.fonts` are both absent in jsdom, so it reports `false` there
 * rather than throwing.
 */
export function useOneLineOverflow<T extends HTMLElement = HTMLParagraphElement>(value: string) {
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<T>(null);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            return;
        }

        let isActive = true;
        const measure = () => {
            if (!isActive) {
                return;
            }
            const lineHeight = resolveLineHeight(el);
            setIsOverflowing(
                el.scrollWidth > el.clientWidth + TOLERANCE_PX ||
                (lineHeight > 0 && el.scrollHeight > lineHeight + TOLERANCE_PX),
            );
        };

        measure();
        document.fonts?.ready.then(measure);

        if (typeof ResizeObserver === 'undefined') {
            return () => { isActive = false; };
        }
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => {
            isActive = false;
            observer.disconnect();
        };
    }, [value]);

    return { textRef, isOverflowing };
}
